import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Save, ShieldCheck, ShieldAlert, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

const EMPTY = {
  organization_name: '', organization_logo: '', organization_description: '',
  social_links: { instagram: '', twitter: '', website: '' },
};

export default function OrganizerProfile() {
  const [role, setRole] = useState(null);
  const [exists, setExists] = useState(false);
  const [verified, setVerified] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authed, setAuthed] = useState(api.isAuthenticated());

  const load = async () => {
    if (!api.isAuthenticated()) { setAuthed(false); setLoading(false); return; }
    try {
      const me = await api.get('/users/me');
      setRole(me.role);
      if (me.role === 'organizer' || me.role === 'admin') {
        try {
          const org = await api.get('/users/me/organizer');
          setExists(true);
          setVerified(!!org.verified);
          setForm({
            organization_name: org.organization_name || '',
            organization_logo: org.organization_logo || '',
            organization_description: org.organization_description || '',
            social_links: {
              instagram: org.social_links?.instagram || '',
              twitter: org.social_links?.twitter || '',
              website: org.social_links?.website || '',
            },
          });
        } catch (_) {
          setExists(false); // 404 => no profile yet
        }
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social_links: { ...f.social_links, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = exists ? await api.put('/users/me/organizer', form)
                           : await api.post('/users/me/organizer', form);
      setExists(true);
      setVerified(!!saved.verified);
      toast.success(exists ? 'Organizer profile updated' : 'Organizer profile created');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!authed) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
        <p className="mb-4">Please log in to manage your organizer profile.</p>
        <Link to="/login"><Button>Go to Login</Button></Link>
      </div>
    );
  }

  if (loading) return <div className="max-w-2xl mx-auto px-4 py-16 text-center opacity-70">Loading…</div>;

  if (role !== 'organizer' && role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <ShieldAlert className="mx-auto mb-4 h-10 w-10 opacity-60" />
        <h1 className="text-xl font-semibold mb-2">Organizer access required</h1>
        <p className="opacity-80">Your account role is <span className="font-medium capitalize">{role === 'user' ? 'Attendee' : role}</span>.
          Ask an administrator to grant you the <span className="font-medium">organizer</span> role to create an organizer profile.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Building2 className="h-6 w-6" /> Organizer Profile</h1>
        {exists && (verified
          ? <Badge className="bg-green-600"><ShieldCheck className="h-3 w-3 mr-1" /> Verified</Badge>
          : <Badge variant="outline" className="border-amber-500 text-amber-600"><ShieldAlert className="h-3 w-3 mr-1" /> Pending verification</Badge>)}
      </div>

      {exists && !verified && (
        <div className="mb-6 rounded-md border border-amber-400/40 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-700 dark:text-amber-300">
          Your organizer profile is not yet verified. Your published events stay hidden from the public until an admin verifies your profile.
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>{exists ? 'Edit organization' : 'Create your organization'}</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="org_name">Organization name</Label>
              <Input id="org_name" value={form.organization_name} onChange={(e) => set('organization_name', e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="org_logo">Logo URL</Label>
              <Input id="org_logo" value={form.organization_logo} onChange={(e) => set('organization_logo', e.target.value)} placeholder="https://…" />
            </div>
            <div>
              <Label htmlFor="org_desc">Description</Label>
              <Textarea id="org_desc" rows={4} value={form.organization_description} onChange={(e) => set('organization_description', e.target.value)} />
            </div>
            <div>
              <Label className="text-sm opacity-70">Social links</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1">
                <Input value={form.social_links.instagram} onChange={(e) => setSocial('instagram', e.target.value)} placeholder="Instagram" />
                <Input value={form.social_links.twitter} onChange={(e) => setSocial('twitter', e.target.value)} placeholder="Twitter / X" />
                <Input value={form.social_links.website} onChange={(e) => setSocial('website', e.target.value)} placeholder="Website" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Link to="/organizer/events" className="text-sm underline opacity-80">My events →</Link>
              <Button type="submit" disabled={saving}><Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : (exists ? 'Save changes' : 'Create profile')}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
